<template>
	<template
		v-if="processedNotifications.length > 0"
		v-for="(notification, index) in processedNotifications"
		:key="notification.id"
	>
		<v-list-item>
			<template #prepend>
				<v-icon size="small" :icon="notification.icon" class="mt-4" />
			</template>

			<div>
				<v-list-item-title>
					{{ translate(notification.title) }}
				</v-list-item-title>

				<v-list-item-subtitle
					class="notifications-card__subtitle"
					@click="$emit('notification-link', notification.id)"
				>
					<span v-if="!showLink(notification)">
						{{ getName(notification) }}
					</span>
					<router-link v-else :to="notification.content.link?.to">
						{{ getName(notification) }}
					</router-link>
				</v-list-item-subtitle>

				<v-list-item-subtitle class="timestamps">
					<v-tooltip
						:text="translate('component.notifications.sentOn')"
						location="top"
					>
						<template #activator="{ props }">
							<v-chip
								color="primary"
								label
								size="x-small"
								v-bind="props"
								prepend-icon="fa-regular fa-paper-plane"
								:text="
									getDateTime(notification.content.created_at)?.dateTimeLocal
								"
							/>
						</template>
					</v-tooltip>

					<v-tooltip
						v-if="mode !== 0 && notification.content.read_at"
						:text="translate('common.readOn')"
						location="top"
					>
						<template #activator="{ props }">
							<v-chip
								class="ml-1"
								color="success"
								label
								size="x-small"
								v-bind="props"
								prepend-icon="fa-regular fa-book-reader"
								:text="getDateTime(notification.content.read_at)?.dateTimeLocal"
							/>
						</template>
					</v-tooltip>
				</v-list-item-subtitle>
			</div>

			<template #append>
				<v-list-item-action>
					<ButtonIcon
						v-if="isAdmin || isTeam"
						:to="{
							name: 'notificationView',
							params: { id: notification.id },
						}"
						:tooltip="{
							text: 'component.notifications.show_notification',
							bottom: true,
						}"
						class="mt-3"
						color="primary"
						icon="fa-regular fa-link"
						icon-size="x-small"
						notext
					/>
					<ButtonIcon
						:disabled="notification.content.read_at !== null"
						:tooltip="{
							text:
								notification.content.read_at !== null
									? 'component.notifications.notification_already_read'
									: 'component.notifications.mark_as_read',
							bottom: true,
						}"
						class="mt-3"
						color="error"
						icon="fa-regular fa-check"
						icon-size="x-small"
						notext
						@click.stop="$emit('mark-as-read', notification.id)"
					/>
				</v-list-item-action>
			</template>
			<v-divider
				v-if="index + 1 < processedNotifications.length"
				:key="`divider_${index}`"
			></v-divider>
		</v-list-item>
	</template>
</template>

<script setup lang="ts">
import { useNotificationProcessor } from '@/composables/useNotificationBuilder.ts'
import { watch, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/system'
import { useTranslate } from 'composables/useTranslate.ts'
import { router } from '@/plugins/router'
import { useDayJs } from '@/composables/dateTime'

interface ProcessedNotification {
	id: string
	title: string
	icon: string
	content: {
		link?: {
			to?: any
			name: string
		}
		read_at?: string | null
		created_at?: string
	}
}

interface Props {
	notifications: Api.Notification[]
	mode: number
}

const { getDateTime } = useDayJs()
const { translate, te } = useTranslate()

const props = defineProps<Props>()
defineEmits<{
	'mark-as-read': [id: string]
	'notification-link': [id: string]
}>()

const { processNotifications } = useNotificationProcessor()
const processedNotifications = ref<ProcessedNotification[]>([])
const { isAdmin, isTeam } = storeToRefs(useUserStore())

function showLink({ content }) {
	return content.link?.to !== undefined && router.hasRoute(content.link.to.name)
}

function getName({ content }) {
	if (content?.link?.to?.name && !router.hasRoute(content.link.to.name))
		return translate('common.noLink.name')
	return te(content.link?.name)
		? translate(content.link?.name)
		: content.link?.name
}

watch(
	() => props.notifications,
	async (data) => {
		processedNotifications.value = await processNotifications(data)
	},
	{ immediate: true }
)
</script>
